import * as Sentry from '@sentry/react';
import { useEffect, useRef } from 'react';
import { isRouteErrorResponse } from 'react-router';

import { clearFetchErrorMeta, resolveFetchErrorMeta } from 'utils/api/fetchErrorMeta';
import { isIgnoredNotFoundApiUrl } from 'utils/api/isIgnoredNotFoundApiUrl';
import { getErrorResponseMessage } from 'utils/errors';

const IGNORED_MESSAGES = new Set(['UNAUTHORIZED', 'No current user', 'SCHEDULED_DOWNTIME', 'Bad Gateway - LOCAL']);
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type PageErrorKind = 'http' | 'network' | 'render';

const GATEWAY_STATUSES = new Set([502, 503, 504]);
const UNREACHABLE_TITLE = "We couldn't reach Clarium. Retrying didn't help.";
const CRASH_TITLE = 'This page ran into a problem.';

type ResolvedPageError = {
  skip: boolean;
  title: string;
  kind: PageErrorKind;
  status?: number;
  method?: string;
  endpoint?: string;
};

const claimedAt = new WeakMap<object, number>();
const sentAt = new WeakMap<object, number>();
const claimedSignatures = new Set<string>();
const sentSignatures = new Set<string>();
let reportGeneration = 0;

class PageRequestError extends Error {
  readonly kind: 'http' | 'network';
  readonly status?: number;
  readonly method: string;
  readonly endpoint: string;

  constructor(kind: 'http' | 'network', method: string, endpoint: string, status?: number) {
    const normalizedMethod = method.toUpperCase();
    const templated = templateEndpoint(endpoint);
    const title =
      kind === 'http'
        ? `HTTP ${status} ${normalizedMethod} ${templated}`
        : `Network error ${normalizedMethod} ${templated}`;
    super(title);
    this.name = 'PageRequestError';
    this.kind = kind;
    this.status = status;
    this.method = normalizedMethod;
    this.endpoint = templated;
  }
}

export function isTimeoutError(error: unknown): error is object {
  return typeof error === 'object' && error !== null && 'name' in error && error.name === 'TimeoutError';
}

export function retryableGet(error: unknown): ResolvedPageError | undefined {
  const resolved = describeError(error);
  if (resolved.method === 'GET' && isUnreachable(resolved)) return resolved;
  return undefined;
}

export function getPageErrorTitle(error: unknown): string {
  const resolved = describeError(error);
  if (isUnreachable(resolved)) return UNREACHABLE_TITLE;
  if (resolved.kind === 'render') return CRASH_TITLE;
  return getErrorResponseMessage(resolved.status);
}

function isUnreachable(resolved: ResolvedPageError): boolean {
  return resolved.kind === 'network' || (resolved.status !== undefined && GATEWAY_STATUSES.has(resolved.status));
}

export function errorFromClientResult(result: { request?: Request; response?: Response }, endpoint: string): Error {
  const method = result.request?.method || 'GET';
  const status = result.response?.status;
  if (typeof status === 'number' && status >= 400) return new PageRequestError('http', method, endpoint, status);
  return new PageRequestError('network', method, endpoint);
}

export function claimPageError(error: unknown): void {
  mark(claimedAt, claimedSignatures, error);
}

export function isPageErrorClaimed(error: unknown): boolean {
  return marked(claimedAt, claimedSignatures, error);
}

export function reportPageError(error: unknown, context: { routeComponent: string }): void {
  const resolved = describeError(error);
  if (marked(sentAt, sentSignatures, error, resolved)) return;
  mark(sentAt, sentSignatures, error, resolved);
  if (resolved.skip) return;

  console.error('reportPageError', {
    error,
    routeComponent: context.routeComponent,
    status: resolved.status,
    endpoint: resolved.endpoint,
    errorKind: resolved.kind,
  });

  const fingerprint = pageErrorFingerprint(resolved);
  Sentry.captureException(toSentryError(error, resolved), {
    tags: {
      routeComponent: context.routeComponent,
      error_kind: resolved.kind,
      ...(resolved.status !== undefined ? { 'http.status': String(resolved.status) } : {}),
      ...(resolved.endpoint ? { endpoint: resolved.endpoint } : {}),
    },
    ...(fingerprint ? { fingerprint } : {}),
    extra: {
      status: resolved.status,
      endpoint: resolved.endpoint,
      method: resolved.method,
      routeError: error,
    },
  });
}

export function useReportPageError(error: unknown, routeComponent: string) {
  claimPageError(error);
  const hasReportedRef = useRef(false);

  useEffect(() => {
    if (hasReportedRef.current) return;
    hasReportedRef.current = true;
    reportPageError(error, { routeComponent });
  }, [error, routeComponent]);
}

export function resetPageErrorReportingForTests(): void {
  reportGeneration += 1;
  claimedSignatures.clear();
  sentSignatures.clear();
  clearFetchErrorMeta();
}

function mark(
  seenAt: WeakMap<object, number>,
  signatures: Set<string>,
  error: unknown,
  resolved = describeError(error)
) {
  if (isObject(error)) seenAt.set(error, reportGeneration);
  const fingerprint = pageErrorFingerprint(resolved);
  if (fingerprint) signatures.add(fingerprint.join('|'));
}

function marked(
  seenAt: WeakMap<object, number>,
  signatures: Set<string>,
  error: unknown,
  resolved = describeError(error)
): boolean {
  if (isObject(error) && seenAt.get(error) === reportGeneration) return true;
  const fingerprint = pageErrorFingerprint(resolved);
  return fingerprint !== undefined && signatures.has(fingerprint.join('|'));
}

function describeError(error: unknown): ResolvedPageError {
  const message = readErrorMessage(error);
  if (message && IGNORED_MESSAGES.has(message)) {
    return { skip: true, title: message, kind: 'render' };
  }

  if (error instanceof PageRequestError) {
    return {
      skip: error.status !== undefined && shouldSkipStatus(error.status, error.endpoint, error),
      title: error.message,
      kind: error.kind,
      status: error.status,
      method: error.method,
      endpoint: error.endpoint,
    };
  }

  const status = isObject(error) ? statusOf(error) : undefined;
  if (isObject(error) && status !== undefined) {
    const meta = resolveFetchErrorMeta(error, status, statusTextOf(error));
    const endpoint = meta ? templateEndpoint(meta.endpoint) : undefined;
    const method = meta?.method.toUpperCase();
    return {
      skip: shouldSkipStatus(status, endpoint, error),
      title: ['HTTP', String(status), method, endpoint].filter(Boolean).join(' '),
      kind: 'http',
      status,
      method,
      endpoint,
    };
  }

  if (error instanceof TypeError && (resolveFetchErrorMeta(error) || isBrowserNetworkMessage(error.message))) {
    return networkDescription(error);
  }

  if (isTimeoutError(error)) return networkDescription(error);

  return { skip: false, title: error instanceof Error ? error.message : String(error), kind: 'render' };
}

function networkDescription(error: object): ResolvedPageError {
  const meta = resolveFetchErrorMeta(error);
  const endpoint = meta ? templateEndpoint(meta.endpoint) : undefined;
  const method = meta?.method.toUpperCase();
  return {
    skip: false,
    title: ['Network error', method, endpoint].filter(Boolean).join(' '),
    kind: 'network',
    method,
    endpoint,
  };
}

function statusOf(error: object): number | undefined {
  if (error instanceof Response || isRouteErrorResponse(error)) return error.status;
  return undefined;
}

function statusTextOf(error: object): string {
  if (error instanceof Response || isRouteErrorResponse(error)) return error.statusText;
  return '';
}

function shouldSkipStatus(status: number, endpoint: string | undefined, error: unknown): boolean {
  if (status === 401 || status === 403) return true;
  if (status !== 404) return false;
  return isIgnoredNotFoundApiUrl(endpoint) || (error instanceof Response && isIgnoredNotFoundApiUrl(error.url));
}

function isBrowserNetworkMessage(message: string): boolean {
  return message === 'Failed to fetch' || message === 'Load failed' || message.includes('NetworkError');
}

function readErrorMessage(error: unknown): string | undefined {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (!isRouteErrorResponse(error)) return undefined;
  if (typeof error.data === 'string') return error.data;

  const inner = error.data && typeof error.data === 'object' && 'error' in error.data ? error.data.error : undefined;
  if (!inner || typeof inner !== 'object' || !('message' in inner) || typeof inner.message !== 'string')
    return undefined;
  return inner.message;
}

function toSentryError(error: unknown, resolved: ResolvedPageError): Error {
  if ((resolved.kind === 'render' && error instanceof Error) || error instanceof PageRequestError) return error;
  const sentryError = new Error(resolved.title);
  if (error instanceof Error && error.stack) sentryError.stack = error.stack;
  return sentryError;
}

function pageErrorFingerprint(resolved: ResolvedPageError): string[] | undefined {
  if ((resolved.kind !== 'http' && resolved.kind !== 'network') || !resolved.endpoint) return undefined;
  return [resolved.kind, String(resolved.status ?? ''), resolved.method ?? '', resolved.endpoint];
}

function templateEndpoint(endpoint: string): string {
  let path = endpoint.split('?')[0] ?? endpoint;
  if (path.startsWith('http://') || path.startsWith('https://')) {
    try {
      path = new URL(path).pathname;
    } catch {
      // keep the raw path
    }
  }

  return path
    .split('/')
    .map((segment) => {
      if (!segment) return segment;
      let decoded: string;
      try {
        decoded = decodeURIComponent(segment);
      } catch {
        decoded = segment;
      }
      if ((decoded.startsWith('{') && decoded.endsWith('}')) || UUID_SEGMENT.test(decoded) || /^\d+$/.test(decoded)) {
        return ':id';
      }
      return segment;
    })
    .join('/');
}

function isObject(error: unknown): error is object {
  return error !== null && typeof error === 'object';
}
